import Fuse from 'fuse.js';
import {
    GDentalExamination,
    GDentalPastProcedure,
    GDentalProcedure,
    GExamination,
    GFollowUp,
    GInjection,
    GLabTest,
    GLabVitalEntity,
    GMedication,
    GProcedure,
    GRefer,
    GSymptomsDiagnosis,
} from '../../store/db';
import { track } from '../../tracking';
import { captureError } from '../../pages/captureError';
import {
    DEFAULT_GENIE_MEMORY_CONFIG,
    LOCAL_ID_PREFIX,
    MAX_G_DENTAL_EXAMINATIONS,
    MAX_G_DENTAL_PAST_PROCEDURES,
    MAX_G_DENTAL_PROCEDURES,
    MAX_G_DIAGNOSIS,
    MAX_G_EXAMINATIONS,
    MAX_G_LAB_TESTS,
    MAX_G_LAB_VITALS,
    MAX_G_LAB_VITALS_TEMPLATES,
    MAX_G_MEDICATIONS,
    MAX_G_PROCEDURES,
    MAX_G_SYMPTOMS,
} from '../../constants';
import { getG, getItemFromMisc, getTemplatesMeta } from '../../store/idbHandler';
import { DB_TABLES } from '../../store/dbTables';
import { GenieSettings } from '../../api/pushGenie';
import { isRXMonetisationT3, isRXMonetisationT4 } from '../../pages/utils';
import { DENTAL_ENTITY_TYPE } from '../../store/types/prescription';
import { ENTITY_TYPE } from './rightPanel/workspace/padV2/LabVitalAutoSuggest';

interface GFollowUpForFuse extends GFollowUp {
    name: string;
}

interface GReferForFuse extends GRefer {
    name: string;
}

export type Item =
    | GSymptomsDiagnosis
    | GMedication
    | GLabTest
    | GInjection
    | GDentalExamination
    | GDentalProcedure
    | GDentalPastProcedure
    | GFollowUpForFuse
    | GReferForFuse
    | GExamination
    | GLabVitalEntity
    | GProcedure;

interface PadGenieAutoSuggestFuseInstance {
    fuse: Fuse<Item>;
    list: Item[];
}

interface PadGenieAutoSuggestFuseInstances {
    [type: string]: PadGenieAutoSuggestFuseInstance;
}

interface FuseConfig {
    keys: string[];
    threshold: number;
}

interface ConfigType {
    type: string;
    fuseConfig: FuseConfig;
    count: number;
    isForTemplate?: boolean;
    customFunction?: (values: Item[][]) => void;
}

export const SYMPTOMS = 'symptoms';
export const DIAGNOSIS = 'diagnosis';
export const MEDICATIONS = 'medications';
export const LAB_TESTS = 'labTests';
export const INJECTIONS = 'injections';
export const DENTAL_CHART = 'dentalChart';
export const DENTAL_EXAMINATIONS = 'dentalExaminations';
export const DENTAL_PROCEDURES = 'dentalProcedures';
export const DENTAL_PAST_PROCEDURES = 'dentalPastProcedures';
export const FOLLOW_UP = 'followUp';
export const DENTAL_EXAMINATIONS_AND_DENTAL_PAST_PROCEDURES =
    'dentalExaminationsAndDentalPastProcedures';
export const REFER = 'refer';
export const EXAMINATIONS = 'examinations';
export const LAB_VITALS = 'labVitals';
export const G_LAB_VITALS_TEMPLATES = 'gLabVitalsTemplates';
export const LAB_VITALS_AND_TEMPLATES = 'labVitalsAndTemplates';
export const PROCEDURES = 'procedures';

type FUSE_AND_LIST_TYPE =
    | typeof SYMPTOMS
    | typeof DIAGNOSIS
    | typeof MEDICATIONS
    | typeof LAB_TESTS
    | typeof INJECTIONS
    | typeof DENTAL_CHART
    | typeof DENTAL_EXAMINATIONS
    | typeof DENTAL_PROCEDURES
    | typeof DENTAL_PAST_PROCEDURES
    | typeof FOLLOW_UP
    | typeof REFER
    | typeof EXAMINATIONS
    | typeof LAB_VITALS
    | typeof G_LAB_VITALS_TEMPLATES
    | typeof DENTAL_EXAMINATIONS_AND_DENTAL_PAST_PROCEDURES
    | typeof LAB_VITALS_AND_TEMPLATES
    | typeof PROCEDURES;

const configs: {
    [k in FUSE_AND_LIST_TYPE]?: ConfigType[];
} = {
    [SYMPTOMS]: [
        {
            type: SYMPTOMS,
            fuseConfig: { keys: ['name', 'icd10_code'], threshold: 0.2 },
            count: MAX_G_SYMPTOMS,
        },
    ],
    [DIAGNOSIS]: [
        {
            type: DIAGNOSIS,
            fuseConfig: { keys: ['name', 'icd10_code'], threshold: 0.2 },
            count: MAX_G_DIAGNOSIS,
        },
    ],
    [MEDICATIONS]: [
        {
            type: MEDICATIONS,
            fuseConfig: { keys: ['name', 'actual_name'], threshold: 0.2 },
            count: MAX_G_MEDICATIONS,
            customFunction: (values: Item[][]) => {
                const gMedicationsElements = sanitizeDrugsAndLabs(values[0]) as GMedication[];
                if (isRXMonetisationT3()) {
                    values[0] = gMedicationsElements.filter(
                        (i) => !i.id.startsWith(LOCAL_ID_PREFIX),
                    );
                }
            },
        },
    ],
    [LAB_TESTS]: [
        {
            type: LAB_TESTS,
            fuseConfig: { keys: ['name', 'actual_name'], threshold: 0.2 },
            count: MAX_G_LAB_TESTS,
            customFunction: (values: Item[][]) => {
                const gLabTestsElements = sanitizeDrugsAndLabs(
                    values[0] as GLabTest[],
                ) as GLabTest[];
                if (isRXMonetisationT3() || isRXMonetisationT4()) {
                    values[0] = gLabTestsElements.filter((i) => !i.id.startsWith(LOCAL_ID_PREFIX));
                }
            },
        },
    ],
    [INJECTIONS]: [
        {
            type: INJECTIONS,
            fuseConfig: { keys: ['name', 'actual_name'], threshold: 0.2 },
            count: MAX_G_MEDICATIONS,
        },
    ],
    [DENTAL_CHART]: [
        {
            type: DENTAL_EXAMINATIONS,
            fuseConfig: { keys: ['name'], threshold: 0.2 },
            count: MAX_G_DENTAL_EXAMINATIONS,
        },
        {
            type: DENTAL_PROCEDURES,
            fuseConfig: { keys: ['name'], threshold: 0.2 },
            count: MAX_G_DENTAL_PROCEDURES,
        },
        {
            type: DENTAL_PAST_PROCEDURES,
            fuseConfig: { keys: ['name'], threshold: 0.2 },
            count: MAX_G_DENTAL_PAST_PROCEDURES,
            customFunction: (values: Item[][]) => {
                const gElements = values[2] || [];
                const gDentalExaminations = values[0] || [];
                const gDentalExaminationsWithType = gDentalExaminations.map((i) => ({
                    ...i,
                    type: DENTAL_ENTITY_TYPE.EXAMINATION,
                }));
                const gDentalPastProceduresWithType = gElements.map((i) => ({
                    ...i,
                    type: DENTAL_ENTITY_TYPE.PROCEDURE,
                }));
                const options = gDentalExaminationsWithType.concat(gDentalPastProceduresWithType);
                const sortedOptions = options.sort(
                    (a, b) => (b.meta?.fCount || 0) - (a.meta?.fCount || 0),
                );
                const instance = PadGenieAutoSuggestFuseManager.getInstance();
                instance.createFuseForType(
                    DENTAL_EXAMINATIONS_AND_DENTAL_PAST_PROCEDURES,
                    sortedOptions,
                    { keys: ['name'], threshold: 0.2 },
                );
            },
        },
    ],
    [REFER]: [
        {
            type: REFER,
            fuseConfig: { keys: ['name', 's_name'], threshold: 0.2 },
            count: Number.MAX_SAFE_INTEGER,
            customFunction: (values: Item[][]) => {
                const gReferElements = values[0] as GRefer[];
                values[0] =
                    gReferElements?.map((option) => ({
                        ...option,
                        name: [option?.doc?.name?.f, option?.doc?.name?.l]
                            .filter(Boolean)
                            .join(' '),
                        doc: {
                            s_name: option?.s_name,
                            s_id: option?.s_id,
                            ...option.doc,
                        },
                    })) || [];
            },
        },
    ],
    [EXAMINATIONS]: [
        {
            type: EXAMINATIONS,
            fuseConfig: { keys: ['name'], threshold: 0.2 },
            count: MAX_G_EXAMINATIONS,
        },
    ],
    [LAB_VITALS]: [
        {
            type: LAB_VITALS,
            fuseConfig: { keys: ['name'], threshold: 0.2 },
            count: MAX_G_LAB_VITALS,
        },
        {
            type: G_LAB_VITALS_TEMPLATES,
            fuseConfig: { keys: ['name'], threshold: 0.2 },
            count: MAX_G_LAB_VITALS_TEMPLATES,
            isForTemplate: true,
            customFunction: (values: Item[][]) => {
                const gLabVitalsElements = values[0] as GLabVitalEntity[];
                const gLabVitals = gLabVitalsElements.map((i) =>
                    i.is_panel
                        ? { ...i, type: ENTITY_TYPE.PANELS }
                        : { ...i, type: ENTITY_TYPE.LAB_RESULTS },
                );
                const templates = values[1].map((template) => ({
                    ...template,
                    isTemplate: true,
                }));
                const instance = PadGenieAutoSuggestFuseManager.getInstance();
                instance.createFuseForType(
                    LAB_VITALS_AND_TEMPLATES,
                    [...gLabVitals, ...templates],
                    { keys: ['name'], threshold: 0.2 },
                );
            },
        },
    ],
    [PROCEDURES]: [
        {
            type: PROCEDURES,
            fuseConfig: { keys: ['name'], threshold: 0.2 },
            count: MAX_G_PROCEDURES,
        },
    ],
};

const availableConfigsSet = new Set(Object.keys(configs));

function sanitizeDrugsAndLabs(items: GMedication[] | GLabTest[]) {
    items.forEach((i) => {
        delete (i as GMedication).hxng_mapped;
        delete (i as GMedication).hxng_status;
        delete (i as GLabTest).book;
    });
    return items;
}

/**
 * @description Can be used only in prescription context,
 */

class PadGenieAutoSuggestFuseManager {
    private static instance: PadGenieAutoSuggestFuseManager;
    private fuseInstances: PadGenieAutoSuggestFuseInstances = {};

    public static getInstance(): PadGenieAutoSuggestFuseManager {
        if (!PadGenieAutoSuggestFuseManager.instance) {
            PadGenieAutoSuggestFuseManager.instance = new PadGenieAutoSuggestFuseManager();
        }
        return PadGenieAutoSuggestFuseManager.instance;
    }

    public createFuseForType(type: string, list: Item[], options: Fuse.IFuseOptions<Item>): void {
        if (!this.fuseInstances) {
            this.fuseInstances = {};
        }
        this.fuseInstances[type] = {
            fuse: new Fuse(list, options),
            list: list,
        };
    }

    public getFuseForType(type: FUSE_AND_LIST_TYPE): Fuse<Item> | undefined {
        return this.fuseInstances[type]?.fuse;
    }

    public getOriginalListForType(type: FUSE_AND_LIST_TYPE): Item[] | undefined {
        return this.fuseInstances[type]?.list;
    }

    public clearAll(): void {
        this.fuseInstances = {};
    }

    public async initializePadGenieAutoSuggestFuse(docid: string): Promise<void> {
        this.clearAll();
        try {
            const settings = await getItemFromMisc<GenieSettings>(docid, 'settings');
            const { padElements = [], genieMemoryConfig = DEFAULT_GENIE_MEMORY_CONFIG } = settings;
            const {
                isInjectionsEnabled,
                isLabTestEnabled,
                isMedicationsEnabled,
            } = genieMemoryConfig;
            const visiblePadElements = padElements.filter((i) => i.isShown).map((i) => i.id);
            const availableConfigs = visiblePadElements
                .filter((i) => availableConfigsSet.has(i))
                .filter((i) => {
                    if (i === INJECTIONS) {
                        return isInjectionsEnabled;
                    }
                    if (i === LAB_TESTS) {
                        return isLabTestEnabled;
                    }
                    if (i === MEDICATIONS) {
                        return isMedicationsEnabled;
                    }
                    return true;
                });
            availableConfigs.forEach((curConfigKey) => {
                const config = configs[curConfigKey as FUSE_AND_LIST_TYPE];
                const pAvailableConfigs = config?.map((j) => {
                    const { count, type, isForTemplate } = j;
                    const key: keyof typeof DB_TABLES = type as keyof typeof DB_TABLES;
                    const gElements = isForTemplate
                        ? getTemplatesMeta(docid, DB_TABLES[key])
                        : getG<Item>(docid, DB_TABLES[key], count);
                    return gElements;
                });
                if (pAvailableConfigs) {
                    Promise.all(pAvailableConfigs)
                        .then((values) => {
                            config?.forEach((k, index) => {
                                let gElements = values[index] || [];
                                const { type, fuseConfig, customFunction } = k;
                                customFunction && customFunction(values);
                                gElements = values[index] || [];
                                this.createFuseForType(type, gElements, fuseConfig);
                            });
                        })
                        .catch((error) => {
                            console.error('Error in Promise.all [pAvailableConfigs]', {
                                message: (error as Error).message,
                            });
                            track('ERROR', {
                                on: 'Promise.all [pAvailableConfi]s)',
                                error: `Error in Promise.all [pAvailableConfig]) ${
                                    (error as Error).message
                                }`,
                            });
                            captureError(
                                new Error(
                                    `Error in Promise.all [pAvailableConfig]) ${
                                        (error as Error).message
                                    }`,
                                ),
                            );
                        });
                }
            });
        } catch (error) {
            this.clearAll();
            console.error('Error in initializePadGenieAutoSuggestFuse', {
                message: (error as Error).message,
            });
            track('ERROR', {
                on: 'initializePadGenieAutoSuggestFuse',
                error: `Error in initializePadGenieAutoSuggestFuse ${(error as Error).message}`,
            });
            captureError(
                new Error(`Error in initializePadGenieAutoSuggestFuse ${(error as Error).message}`),
            );
        }
    }
}

export default PadGenieAutoSuggestFuseManager.getInstance();
